import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import { getAPIErrorMessage } from '@/utils/request';
import { message } from 'antd';
import { useCallback } from 'react';
import { useDispatch } from 'umi';

/**
 * Persisting wording for one key — any number of languages, in one request — as a
 * promise.
 *
 * The model's `saveTranslation` effect reports through `onSuccess` / `onError`
 * callbacks, so four surfaces had each wrapped the same dispatch in the same
 * `new Promise` — and the two that did *not* wrap it ended up nesting their
 * bookkeeping inside the callbacks instead, which is the same code shaped worse.
 *
 * The hook owns only the wrapping. What to do afterwards — clear a local override,
 * drop a saving flag, close a popover — stays at the call site, because each surface
 * genuinely does something different there.
 *
 * Resolves **false** rather than rejecting: a failed save is an expected outcome
 * (a 422 on a key the partner may not own), and every caller wants to re-enable a
 * button rather than handle an exception.
 *
 * **The failure is reported here, not by the caller.** The model effect passes its
 * `onError` straight through, which suppresses `processAPIResponse`'s own toast — so
 * three of the four surfaces used to drop a 422 on the floor and simply un-press the
 * button, leaving the wording apparently saved. Reporting it in the wrapper is what
 * makes the boolean mean "it failed, and the user has been told why"; the caller still
 * decides what to *do* about it.
 */
export function useSaveTranslation(): (
  fullKey: string,
  values: Record<string, string>,
) => Promise<boolean> {
  const dispatch = useDispatch();

  return useCallback(
    (fullKey: string, values: Record<string, string>) =>
      new Promise<boolean>((resolve) => {
        dispatch({
          type: EMAIL_CENTER_ACTIONS.SAVE_TRANSLATION,
          // A map, always — one request per save. The effect merges into the catalog's
          // copy of the value map, so two overlapping saves would each drop the other's
          // languages.
          payload: {
            fullKey,
            values,
            onSuccess: () => resolve(true),
            onError: (error: unknown) => {
              // The server's own words: a rejected key names the group it
              // belongs to, which is the one thing a generic "could not save"
              // cannot say.
              message.error(getAPIErrorMessage(error));
              resolve(false);
            },
          },
        });
      }),
    [dispatch],
  );
}
